import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Grid, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, FormControl, InputLabel,
  Select, MenuItem, Card, CardContent, Typography, Box,
  Chip, Dialog, DialogTitle, DialogContent, IconButton,
  Collapse, Tooltip, Alert, CircularProgress
} from '@mui/material';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Importar iconos de FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronDown,
  faSearch,
  faFilePdf,
  faFileExcel,
  faStore,
  faCalendar,
  faReceipt,
  faExclamationTriangle,
  faMoneyBill,
  faCreditCard,
  faTrash,
  faPrint,
  faCheckCircle,
  faTimesCircle,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';

function ReporteCortez() {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroTienda, setFiltroTienda] = useState("");
  const [filtroSerie, setFiltroSerie] = useState("");
  const [tiendas, setTiendas] = useState([]);
  const [series, setSeries] = useState([]);
  const [datosAPI, setDatosAPI] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tiendasPrecargadas = [
    { id: 1, nombre: 'TZ10', ubicacion: 'Zona 10' },
    { id: 2, nombre: 'TPRR', ubicacion: 'Pradera' },
    { id: 3, nombre: 'TSCR', ubicacion: 'Santa Catarina' },
    { id: 4, nombre: 'TCSV', ubicacion: 'San Cristóbal' },
    { id: 5, nombre: 'TMIR', ubicacion: 'Miraflores' },
    { id: 6, nombre: 'TXLA', ubicacion: 'Xela' }
  ];

  useEffect(() => {
    setTiendas(tiendasPrecargadas);
  }, []);

  useEffect(() => {
    const nombreUsuario = user?.usuario || '';
    const tiendasFiltradas = tiendasPrecargadas.filter(tienda =>
      tienda.nombre.includes(nombreUsuario)
    );

    if (tiendasFiltradas.length > 0) {
      setTiendas(tiendasFiltradas);
      setFiltroTienda(tiendasFiltradas[0].nombre);
    } else {
      setTiendas(tiendasPrecargadas);
      setFiltroTienda("");
    }
  }, [user]);

  useEffect(() => {
    if (datosAPI && filtroTienda) {
      const seriesDeTienda = datosAPI
        .filter(item => item?.Establecimiento === filtroTienda)
        .map(item => item?.Series)
        .filter(Boolean);
      
      const seriesUnicas = [...new Set(seriesDeTienda)];
      setSeries(seriesUnicas);
      setFiltroSerie("");
    } else if (datosAPI) {
      const todasLasSeries = [...new Set(datosAPI.map(item => item?.Series).filter(Boolean))];
      setSeries(todasLasSeries);
    }
  }, [datosAPI, filtroTienda]);

  // Calcular totales
  const calcularTotales = () => {
    if (!datosFiltrados?.length) return null;
    
    return datosFiltrados.reduce((acc, row) => {
      // Asegurar que todos los valores sean números
      const safeNumber = (val) => typeof val === 'number' ? val : parseFloat(val) || 0;
      
      return {
        EFECTIVO: acc.EFECTIVO + safeNumber(row.EFECTIVO),
        CHEQUES: acc.CHEQUES + safeNumber(row.CHEQUES),
        CREDOMATIC: acc.CREDOMATIC + safeNumber(row.CREDOMATIC),
        CUPON: acc.CUPON + safeNumber(row.CUPON),
        DEGUSTACIONES: acc.DEGUSTACIONES + safeNumber(row.DEGUSTACIONES),
        EMPLEADOS: acc.EMPLEADOS + safeNumber(row.EMPLEADOS),
        EXENCION_DE_IVA: acc.EXENCION_DE_IVA + safeNumber(row.EXENCION_DE_IVA),
        NEO_NET_POS: acc.NEO_NET_POS + safeNumber(row.NEO_NET_POS),
        NEO_NET_LINK: acc.NEO_NET_LINK + safeNumber(row.NEO_NET_LINK),
        PEDIDOS_YA: acc.PEDIDOS_YA + safeNumber(row.PEDIDOS_YA),
        TIENDA_EN_LINEA_Shopify: acc.TIENDA_EN_LINEA_Shopify + safeNumber(row.TIENDA_EN_LINEA_Shopify),
        TRANSFERENCIA: acc.TRANSFERENCIA + safeNumber(row.TRANSFERENCIA),
        TRANSFER_DM: acc.TRANSFER_DM + safeNumber(row.TRANSFER_DM),
        VISA_NET: acc.VISA_NET + safeNumber(row.VISA_NET),
        CUPON_DM: acc.CUPON_DM + safeNumber(row.CUPON_DM),
        TOTAL_DIA: acc.TOTAL_DIA + safeNumber(row.TOTAL_DIA),
        FACTURAS_anuladas: acc.FACTURAS_anuladas + safeNumber(row.FACTURAS_anuladas),
        FACTURAS_anuladas_TOTAL: acc.FACTURAS_anuladas_TOTAL + safeNumber(row.FACTURAS_anuladas_TOTAL),
        totalFacturas: acc.totalFacturas + (Array.isArray(row.DETALLE) ? row.DETALLE.length : 0)
      };
    }, {
      EFECTIVO: 0, CHEQUES: 0, CREDOMATIC: 0, CUPON: 0, DEGUSTACIONES: 0,
      EMPLEADOS: 0, EXENCION_DE_IVA: 0, NEO_NET_POS: 0, NEO_NET_LINK: 0,
      PEDIDOS_YA: 0, TIENDA_EN_LINEA_Shopify: 0, TRANSFERENCIA: 0,
      TRANSFER_DM: 0, VISA_NET: 0, CUPON_DM: 0, TOTAL_DIA: 0,
      FACTURAS_anuladas: 0, FACTURAS_anuladas_TOTAL: 0,
      totalFacturas: 0
    });
  };

  const fetchData = () => {
    if (!fechaInicio || !fechaFin) {
      setError("Por favor seleccione ambas fechas");
      return;
    }

    setLoading(true);
    setError(null);

    // Construir el objeto de datos para la API incluyendo la tienda seleccionada
    const datosMetodo = {
      Vista: "ReporteCierres",
      FechaIni: fechaInicio,
      FechaFin: fechaFin
    };

    // Agregar tienda solo si está seleccionada
    if (filtroTienda) {
      datosMetodo.Establecimiento = filtroTienda;
    }

    const datos = {
      Token: "LJKNFUH2FWEJBAEFFFFFKJ VGDEA003CSFCAQWEUIRRRRRRRRRRRADFYGBIFHVBESRNYHIOPWJROTY4WY",
      Ambiente: "DESA",
      Dispositivo: {
        Marca: "SAMSUNG",
        Modelo: "S22002B",
        Version: "Android 18.125",
        McAdress: null,
        PowerStatus: "84%",
        WifiStatus: "Conected to iphone de Mauricio",
        GPSStatus: "ON",
        GPS: "321|-485|321"
      },
      Usr: {
        Codigo: "1234",
        UserNickName: user?.usuario || ''
      },
      DatosMetodo: {
        NombreMetodo: "ListaReportesSalas",
        Datos_Recibidos: JSON.stringify(datosMetodo)
      }
    };

    console.log("Enviando a API:", datos);
    
    const fetchDataUrl = `https://appcorp.parma.com.gt/API/ReceptorDatos?Datos=${encodeURIComponent(JSON.stringify(datos))}`;

    fetch(fetchDataUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log("Respuesta API:", data);
        
        if (data && data.datos_Generados) {
          try {
            // Limpiar y parsear los datos generados
            let datosGeneradosLimpiados = data.datos_Generados;
            
            // Remover escapes dobles si existen
            datosGeneradosLimpiados = datosGeneradosLimpiados.replace(/\\\\"/g, '"');
            datosGeneradosLimpiados = datosGeneradosLimpiados.replace(/\\\\/g, '\\');
            
            // Si el string comienza y termina con comillas, removerlas
            if (datosGeneradosLimpiados.startsWith('"') && datosGeneradosLimpiados.endsWith('"')) {
              datosGeneradosLimpiados = datosGeneradosLimpiados.slice(1, -1);
            }
            
            // Reemplazar escapes de comillas
            datosGeneradosLimpiados = datosGeneradosLimpiados.replace(/\\"/g, '"');
            
            console.log("String limpio:", datosGeneradosLimpiados.substring(0, 200) + "...");
            
            const datosGeneradosDecodificados = JSON.parse(datosGeneradosLimpiados);
            console.log("Datos decodificados:", datosGeneradosDecodificados);
            
            // Asegurar que sea un array
            const datosArray = Array.isArray(datosGeneradosDecodificados) 
              ? datosGeneradosDecodificados 
              : [datosGeneradosDecodificados];
            
            setDatosAPI(datosArray);
            setError(null);
          } catch (error) {
            console.error("Error al decodificar los datos generados:", error);
            console.error("String que causó el error:", data.datos_Generados?.substring(0, 500));
            setError("Error al procesar los datos recibidos. Formato incorrecto.");
          }
        } else {
          console.error("No se recibieron datos generados válidos:", data);
          setError(data?.mensaje || "No se recibieron datos de la API");
          setDatosAPI([]);
        }
      })
      .catch(error => {
        console.error('Error en fetch:', error);
        setError(`Error en la conexión: ${error.message}`);
        setDatosAPI([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearch = () => {
    fetchData();
  };

  const formatCurrency = (value) => {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatNumber = (value) => {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    return new Intl.NumberFormat('es-GT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const safeNumber = (value) => typeof value === 'number' ? value : parseFloat(value) || 0;

  const esFacturaAnulada = (factura) => {
    const estado = String(factura?.EstatusFactura || '').trim().toUpperCase();
    return estado && estado !== 'V';
  };

  const getFacturasAnuladas = (row) => {
    if (Array.isArray(row?.FACTURAS_anuladas_DETALLE)) {
      return row.FACTURAS_anuladas_DETALLE;
    }

    return Array.isArray(row?.DETALLE) ? row.DETALLE.filter(esFacturaAnulada) : [];
  };

  const getFacturasVigentes = (row) => {
    return Array.isArray(row?.DETALLE)
      ? row.DETALLE.filter(factura => String(factura?.EstatusFactura || '').trim().toUpperCase() === 'V')
      : [];
  };

  const getTotalAnulado = (row) => {
    const totalResumen = safeNumber(row?.FACTURAS_anuladas_TOTAL);
    if (totalResumen !== 0) return totalResumen;

    return getFacturasAnuladas(row).reduce((total, factura) => {
      return total + safeNumber(factura.TOTALGROSSAMOUNT || factura.DocTotal || factura.Total);
    }, 0);
  };

  // Filtrar datos según los filtros seleccionados
  const datosFiltrados = datosAPI && Array.isArray(datosAPI)
    ? datosAPI
        .filter(row => {
          if (!row) return false;
          
          const cumpleTienda = !filtroTienda || row.Establecimiento === filtroTienda;
          const cumpleSerie = !filtroSerie || row.Series === filtroSerie;
          
          // Búsqueda por texto en todos los campos
          const cumpleTexto = !filtroTexto || 
            Object.entries(row).some(([key, val]) => {
              if (val === null || val === undefined) return false;
              return String(val).toLowerCase().includes(filtroTexto.toLowerCase());
            });
          
          return cumpleTienda && cumpleSerie && cumpleTexto;
        })
        .sort((a, b) => {
          // Ordenar por fecha descendente
          const dateA = a.DocDate ? new Date(a.DocDate) : new Date(0);
          const dateB = b.DocDate ? new Date(b.DocDate) : new Date(0);
          return dateB - dateA;
        })
    : [];

  const totales = calcularTotales();

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleExpandAll = () => {
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);
    
    const newExpandedRows = {};
    datosFiltrados.forEach((_, index) => {
      newExpandedRows[index] = newExpandAll;
    });
    setExpandedRows(newExpandedRows);
  };

const exportToExcel = () => {
  if (!datosFiltrados.length) {
    alert("No hay datos para exportar");
    return;
  }

  const limpiarNombreHoja = (nombre, usados = new Set()) => {
    const base = String(nombre || "Sin tienda")
      .replace(/[\\/?*[\]:]/g, " ")
      .trim()
      .substring(0, 31) || "Sin tienda";

    let nombreFinal = base;
    let contador = 1;

    while (usados.has(nombreFinal)) {
      const sufijo = ` ${contador}`;
      nombreFinal = `${base.substring(0, 31 - sufijo.length)}${sufijo}`;
      contador += 1;
    }

    usados.add(nombreFinal);
    return nombreFinal;
  };

  try {
    const workbook = XLSX.utils.book_new();
    const hojasUsadas = new Set();

    const datosResumen = datosFiltrados.map(row => ({
      Fecha: row.DocDate ? new Date(row.DocDate).toLocaleDateString() : "",
      Tienda: row.Establecimiento || "",
      Serie: row.Series || "",
      EFECTIVO: row.EFECTIVO || 0,
      CHEQUES: row.CHEQUES || 0,
      CREDOMATIC: row.CREDOMATIC || 0,
      CUPON: row.CUPON || 0,
      DEGUSTACIONES: row.DEGUSTACIONES || 0,
      EMPLEADOS: row.EMPLEADOS || 0,
      EXENCION_IVA: row.EXENCION_DE_IVA || 0,
      NEO_NET_POS: row.NEO_NET_POS || 0,
      NEO_NET_LINK: row.NEO_NET_LINK || 0,
      PEDIDOS_YA: row.PEDIDOS_YA || 0,
      TIENDA_EN_LINEA: row.TIENDA_EN_LINEA_Shopify || 0,
      TRANSFERENCIA: row.TRANSFERENCIA || 0,
      TRANSFER_DM: row.TRANSFER_DM || 0,
      VISA_NET: row.VISA_NET || 0,
      CUPON_DM: row.CUPON_DM || 0,
      TOTAL_DIA: row.TOTAL_DIA || 0,
      FACTURAS_ANULADAS: row.FACTURAS_anuladas || 0,
      TOTAL_ANULADO: getTotalAnulado(row)
    }));

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(datosResumen),
      "Resumen"
    );
    hojasUsadas.add("Resumen");

    const datosPorTienda = datosFiltrados.reduce((acc, row) => {
      const tienda = row.Establecimiento || "Sin tienda";
      if (!acc[tienda]) acc[tienda] = [];
      acc[tienda].push(row);
      return acc;
    }, {});

    Object.entries(datosPorTienda).forEach(([tienda, cortes]) => {
      const detalleTienda = [];

      cortes.forEach(row => {
        const facturasVigentes = getFacturasVigentes(row);
        const facturasAnuladas = getFacturasAnuladas(row);

        facturasVigentes.forEach(det => {
          detalleTienda.push({
            Tipo: "Vigente",
            FechaCorte: row.DocDate ? new Date(row.DocDate).toLocaleDateString() : "",
            Tienda: row.Establecimiento || "",
            Serie: row.Series || "",
            Factura: det.DocNum || det.ID || "",
            CodigoCliente: det.CardCode || "",
            Cliente: det.CardName || "N/A",
            Total: safeNumber(det.DocTotal || det.Total || det.TOTALGROSSAMOUNT),
            Tarjeta: det.TarjetaNombre || "N/A",
            Cuenta: det.AcctName || "N/A",
            Estado: det.EstatusFactura || det.STATUS || "",
            Canal: det.Canal || "N/A",
            FacturaOriginal: det.ORIGINAL_RECEIPT_ID || "",
            FechaAnulacion: det.CREATEDAT ? new Date(det.CREATEDAT).toLocaleString() : "",
            Caja: det.POS_SYSTEM_NAME || "",
            SerieAnulacion: det.SerieCajaAnulacion || det.SeriesName || ""
          });
        });

        facturasAnuladas.forEach(det => {
          detalleTienda.push({
            Tipo: "Anulada",
            FechaCorte: row.DocDate ? new Date(row.DocDate).toLocaleDateString() : "",
            Tienda: row.Establecimiento || "",
            Serie: row.Series || "",
            Factura: det.ID || det.DocNum || "",
            CodigoCliente: det.CardCode || "",
            Cliente: det.CardName || "N/A",
            Total: safeNumber(det.TOTALGROSSAMOUNT || det.DocTotal || det.Total),
            Tarjeta: det.TarjetaNombre || "N/A",
            Cuenta: det.AcctName || "N/A",
            Estado: det.STATUS || det.EstatusFactura || "Anulada",
            Canal: det.Canal || "N/A",
            FacturaOriginal: det.ORIGINAL_RECEIPT_ID || "",
            FechaAnulacion: det.CREATEDAT ? new Date(det.CREATEDAT).toLocaleString() : "",
            Caja: det.POS_SYSTEM_NAME || "",
            SerieAnulacion: det.SerieCajaAnulacion || det.SeriesName || ""
          });
        });
      });

      const hojaDetalle = XLSX.utils.json_to_sheet(
        detalleTienda.length
          ? detalleTienda
          : [{ Mensaje: "No hay detalle de facturas para esta tienda" }]
      );

      XLSX.utils.book_append_sheet(
        workbook,
        hojaDetalle,
        limpiarNombreHoja(`Detalle ${tienda}`, hojasUsadas)
      );
    });

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(data, `ReporteCortez_${new Date().toISOString().split("T")[0]}.xlsx`);
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    alert("Error al exportar a Excel");
  }
};

  const exportToPDF = () => {
    if (!datosFiltrados.length) {
      alert("No hay datos para exportar");
      return;
    }

    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text('Reporte de Corte Z', 14, 22);
      doc.setFontSize(11);
      doc.text(`Generado: ${new Date().toLocaleDateString()}`, 14, 30);
      
      if (fechaInicio && fechaFin) {
        doc.text(`Período: ${fechaInicio} al ${fechaFin}`, 14, 38);
      }

      doc.autoTable({
        startY: 45,
        head: [['Fecha', 'Tienda', 'Serie', 'Efectivo', 'Credomatic', 'VisaNet', 'Total Día', 'Fact. Anuladas', 'Total Anulado']],
        body: datosFiltrados.map(row => [
          row.DocDate ? new Date(row.DocDate).toLocaleDateString() : '',
          row.Establecimiento || '',
          row.Series || '',
          formatCurrency(row.EFECTIVO),
          formatCurrency(row.CREDOMATIC),
          formatCurrency(row.VISA_NET),
          formatCurrency(row.TOTAL_DIA),
          row.FACTURAS_anuladas || 0,
          formatCurrency(row.FACTURAS_anuladas_TOTAL || 0)
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [44, 62, 80] }
      });

      doc.save(`ReporteCortez_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      alert("Error al exportar a PDF");
    }
  };

  const imprimirDetalle = (row) => {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(16);
      doc.text('Detalle de Corte Z', 14, 20);
      doc.setFontSize(12);
      doc.text(`Tienda: ${row.Establecimiento || ''} - Serie: ${row.Series || ''}`, 14, 30);
      doc.text(`Fecha: ${row.DocDate ? new Date(row.DocDate).toLocaleDateString() : ''}`, 14, 38);
      
      const metodosPago = [
        ['Efectivo', formatCurrency(row.EFECTIVO)],
        ['Cheques', formatCurrency(row.CHEQUES)],
        ['Credomatic', formatCurrency(row.CREDOMATIC)],
        ['Cupón', formatCurrency(row.CUPON)],
        ['Degustaciones', formatCurrency(row.DEGUSTACIONES)],
        ['Empleados', formatCurrency(row.EMPLEADOS)],
        ['Exención IVA', formatCurrency(row.EXENCION_DE_IVA)],
        ['NEO NET POS', formatCurrency(row.NEO_NET_POS)],
        ['NEO NET LINK', formatCurrency(row.NEO_NET_LINK)],
        ['Pedidos Ya', formatCurrency(row.PEDIDOS_YA)],
        ['Tienda en Línea', formatCurrency(row.TIENDA_EN_LINEA_Shopify)],
        ['Transferencia', formatCurrency(row.TRANSFERENCIA)],
        ['Transfer DM', formatCurrency(row.TRANSFER_DM)],
        ['Visa Net', formatCurrency(row.VISA_NET)],
        ['Cupón DM', formatCurrency(row.CUPON_DM)]
      ].filter(([_, valor]) => valor !== 'Q0.00');

      doc.autoTable({
        startY: 45,
        head: [['Método de Pago', 'Monto']],
        body: metodosPago,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [44, 62, 80] }
      });

      const finalY = doc.lastAutoTable.finalY;

      const facturasAnuladas = getFacturasAnuladas(row);

      if (row.FACTURAS_anuladas > 0 || facturasAnuladas.length > 0) {
        doc.text(`Facturas Anuladas: ${row.FACTURAS_anuladas || facturasAnuladas.length} - Total Anulado: ${formatCurrency(getTotalAnulado(row))}`, 
          14, finalY + 10);
      }

      if (row.DETALLE && Array.isArray(row.DETALLE) && row.DETALLE.length > 0) {
        doc.addPage();
        doc.setFontSize(14);
        doc.text('Detalle de Facturas', 14, 20);
        
        const facturasData = row.DETALLE.map(det => [
          det.DocNum || '',
          det.CardCode || '',
          det.CardName?.substring(0, 25) || 'N/A',
          formatCurrency(det.DocTotal || det.Total || 0),
          det.TarjetaNombre || 'N/A',
          det.AcctName || 'N/A',
          det.EstatusFactura || '',
          det.Canal || 'N/A'
        ]);

        doc.autoTable({
          startY: 25,
          head: [['Factura', 'Código', 'Cliente', 'Total', 'Tarjeta', 'Cuenta', 'Estado', 'Canal']],
          body: facturasData,
          styles: { fontSize: 7 },
          headStyles: { fillColor: [44, 62, 80] }
        });
      }

      if (facturasAnuladas.length > 0) {
        doc.addPage();
        doc.setFontSize(14);
        doc.text('Detalle de Facturas Anuladas', 14, 20);

        const facturasAnuladasData = facturasAnuladas.map(det => [
          det.ID || det.DocNum || '',
          det.ORIGINAL_RECEIPT_ID || '',
          det.CREATEDAT ? new Date(det.CREATEDAT).toLocaleString() : '',
          formatCurrency(det.TOTALGROSSAMOUNT || det.DocTotal || det.Total || 0),
          det.POS_SYSTEM_NAME || det.TarjetaNombre || 'N/A',
          det.SerieCajaAnulacion || det.SeriesName || 'N/A',
          det.STATUS || det.EstatusFactura || 'Anulada',
          det.Canal || 'N/A'
        ]);

        doc.autoTable({
          startY: 25,
          head: [['Factura Anulada', 'Factura Original', 'Fecha Anulacion', 'Total', 'Caja', 'Serie', 'Estado', 'Canal']],
          body: facturasAnuladasData,
          styles: { fontSize: 7 },
          headStyles: { fillColor: [211, 47, 47] }
        });
      }

      doc.save(`Detalle_${row.Establecimiento || 'SinTienda'}_${row.Series || 'SinSerie'}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error("Error printing detail:", error);
      alert("Error al imprimir detalle");
    }
  };

  const renderDetalleFacturas = (detalle) => {
    if (!detalle || !Array.isArray(detalle) || detalle.length === 0) return null;
    
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <FontAwesomeIcon icon={faReceipt} style={{ color: '#1976d2' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Panel 1 - Detalle V
          </Typography>
          <Chip 
            label={`${detalle.length} registros`} 
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Factura</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Cliente</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Tarjeta</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Cuenta</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Canal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detalle.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.DocNum || ''}</TableCell>
                  <TableCell>{item.CardCode || ''}</TableCell>
                  <TableCell>{item.CardName || 'N/A'}</TableCell>
                  <TableCell align="right">{formatCurrency(item.DocTotal || item.Total || 0)}</TableCell>
                  <TableCell>
                    {item.TarjetaNombre || 'N/A'}
                    {item.TarjetaNombre?.toLowerCase().includes('efectivo') && (
                      <FontAwesomeIcon icon={faMoneyBill} style={{ marginLeft: 4, color: '#4caf50' }} size="xs" />
                    )}
                  </TableCell>
                  <TableCell>{item.AcctName || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={item.EstatusFactura || 'N/A'} 
                      size="small"
                      color={item.EstatusFactura === 'V' ? 'success' : 'error'}
                      variant="outlined"
                      icon={item.EstatusFactura === 'V' ? 
                        <FontAwesomeIcon icon={faCheckCircle} /> : 
                        <FontAwesomeIcon icon={faTimesCircle} />
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {item.Canal || 'N/A'}
                    {item.Canal === 'DOM' && (
                      <FontAwesomeIcon icon={faExchangeAlt} style={{ marginLeft: 4, color: '#ff9800' }} size="xs" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderDetalleFacturasAnuladas = (detalle) => {
    if (!detalle || !Array.isArray(detalle) || detalle.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fffafa' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <FontAwesomeIcon icon={faTrash} style={{ color: '#d32f2f' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
              Panel 2 - Detalle Anulado
            </Typography>
            <Chip label="0 registros" size="small" color="error" variant="outlined" />
          </Box>
          <Typography variant="body2" color="textSecondary">
            No hay facturas anuladas para este corte
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fffafa' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <FontAwesomeIcon icon={faTrash} style={{ color: '#d32f2f' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
            Panel 2 - Detalle Anulado
          </Typography>
          <Chip
            label={`${detalle.length} registros`}
            size="small"
            color="error"
            variant="outlined"
          />
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Factura Anulada</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Factura Original</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Fecha AnulaciÃ³n</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Caja</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Serie</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#ffebee' }}>Canal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detalle.map((item, idx) => (
                <TableRow key={idx} sx={{ bgcolor: '#fff5f5' }}>
                  <TableCell>{item.ID || item.DocNum || ''}</TableCell>
                  <TableCell>{item.ORIGINAL_RECEIPT_ID || 'N/A'}</TableCell>
                  <TableCell>{item.CREATEDAT ? new Date(item.CREATEDAT).toLocaleString() : 'N/A'}</TableCell>
                  <TableCell align="right">{formatCurrency(item.TOTALGROSSAMOUNT || item.DocTotal || item.Total || 0)}</TableCell>
                  <TableCell>{item.POS_SYSTEM_NAME || item.TarjetaNombre || 'N/A'}</TableCell>
                  <TableCell>{item.SerieCajaAnulacion || item.SeriesName || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.STATUS || item.EstatusFactura || 'Anulada'}
                      size="small"
                      color="error"
                      variant="outlined"
                      icon={<FontAwesomeIcon icon={faTimesCircle} />}
                    />
                  </TableCell>
                  <TableCell>{item.Canal || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Tarjeta de filtros */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FontAwesomeIcon icon={faStore} size="2x" style={{ color: '#1976d2' }} />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                Reporte de Corte Z
              </Typography>
            </Box>
            
            {user && (
              <Chip 
                label={`Usuario: ${user.usuario || 'Sin usuario'}`} 
                color="primary" 
                size="small"
              />
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                label="Buscar"
                value={filtroTexto}
                onChange={(e) => setFiltroTexto(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Buscar en resultados..."
                InputProps={{
                  startAdornment: (
                    <FontAwesomeIcon icon={faSearch} style={{ marginRight: 8, color: '#999' }} />
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tienda</InputLabel>
                <Select
                  value={filtroTienda}
                  onChange={(e) => setFiltroTienda(e.target.value)}
                  label="Tienda"
                >
                  <MenuItem value="">Todas las tiendas</MenuItem>
                  {tiendas.map((tienda) => (
                    <MenuItem key={tienda.id} value={tienda.nombre}>
                      {tienda.nombre} - {tienda.ubicacion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Serie</InputLabel>
                <Select
                  value={filtroSerie}
                  onChange={(e) => setFiltroSerie(e.target.value)}
                  label="Serie"
                  disabled={!datosAPI || series.length === 0}
                >
                  <MenuItem value="">Todas las series</MenuItem>
                  {series.map((serie, index) => (
                    <MenuItem key={index} value={serie}>
                      {serie}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                label="Fecha Inicio"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 8, color: '#999' }} />
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                label="Fecha Fin"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  fullWidth
                  startIcon={<FontAwesomeIcon icon={faSearch} />}
                  disabled={loading}
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
                <Tooltip title="Exportar a PDF">
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={exportToPDF}
                    disabled={!datosFiltrados.length || loading}
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                  </Button>
                </Tooltip>
                <Tooltip title="Exportar a Excel">
                  <Button
                    variant="contained"
                    color="success"
                    onClick={exportToExcel}
                    disabled={!datosFiltrados.length || loading}
                  >
                    <FontAwesomeIcon icon={faFileExcel} />
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tabla de resultados */}
      {datosFiltrados.length > 0 && (
        <Card sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white', width: '100px' }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <IconButton 
                        size="small" 
                        onClick={toggleExpandAll}
                        sx={{ color: 'white', padding: '2px' }}
                      >
                        <FontAwesomeIcon icon={expandAll ? faChevronDown : faChevronRight} />
                      </IconButton>
                      Acciones
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Tienda</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Serie</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Efectivo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Cheques</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Credomatic</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Cupón</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Degust.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Empleados</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Exención IVA</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>NeoNet POS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>NeoNet Link</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Pedidos Ya</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Tienda Línea</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Transferencia</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Transfer DM</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>VisaNet</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Cupón DM</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Total Día</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Fact. Anul</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Total Anul</TableCell>
                </TableRow>

                {/* Fila de totales */}
                {totales && (
                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                    <TableCell sx={{ fontWeight: 'bold' }} colSpan={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FontAwesomeIcon icon={faMoneyBill} style={{ color: '#1976d2' }} />
                        <span>TOTALES GENERALES:</span>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.EFECTIVO)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.CHEQUES)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.CREDOMATIC)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.CUPON)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.DEGUSTACIONES)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.EMPLEADOS)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.EXENCION_DE_IVA)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.NEO_NET_POS)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.NEO_NET_LINK)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.PEDIDOS_YA)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.TIENDA_EN_LINEA_Shopify)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.TRANSFERENCIA)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.TRANSFER_DM)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.VISA_NET)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totales.CUPON_DM)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976d2' }}>{formatNumber(totales.TOTAL_DIA)}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{totales.FACTURAS_anuladas}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>{formatNumber(totales.FACTURAS_anuladas_TOTAL)}</TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {datosFiltrados.map((row, index) => (
                  <React.Fragment key={index}>
                    <TableRow 
                      hover
                      sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                    >
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(index)}
                            sx={{ 
                              transform: expandedRows[index] ? 'rotate(90deg)' : 'none',
                              transition: '0.2s',
                              padding: '2px'
                            }}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleRowClick(row)}
                            sx={{ padding: '2px' }}
                          >
                            <FontAwesomeIcon icon={faSearch} size="xs" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => imprimirDetalle(row)}
                            sx={{ padding: '2px' }}
                          >
                            <FontAwesomeIcon icon={faPrint} size="xs" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>{row.DocDate ? new Date(row.DocDate).toLocaleDateString() : ''}</TableCell>
                      <TableCell>
                        <Chip 
                          label={row.Establecimiento || ''} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.Series || ''}</TableCell>
                      <TableCell align="right">{formatNumber(row.EFECTIVO)}</TableCell>
                      <TableCell align="right">{formatNumber(row.CHEQUES)}</TableCell>
                      <TableCell align="right">{formatNumber(row.CREDOMATIC)}</TableCell>
                      <TableCell align="right">{formatNumber(row.CUPON)}</TableCell>
                      <TableCell align="right">{formatNumber(row.DEGUSTACIONES)}</TableCell>
                      <TableCell align="right">{formatNumber(row.EMPLEADOS)}</TableCell>
                      <TableCell align="right">{formatNumber(row.EXENCION_DE_IVA)}</TableCell>
                      <TableCell align="right">{formatNumber(row.NEO_NET_POS)}</TableCell>
                      <TableCell align="right">{formatNumber(row.NEO_NET_LINK)}</TableCell>
                      <TableCell align="right">{formatNumber(row.PEDIDOS_YA)}</TableCell>
                      <TableCell align="right">{formatNumber(row.TIENDA_EN_LINEA_Shopify)}</TableCell>
                      <TableCell align="right">{formatNumber(row.TRANSFERENCIA)}</TableCell>
                      <TableCell align="right">{formatNumber(row.TRANSFER_DM)}</TableCell>
                      <TableCell align="right">{formatNumber(row.VISA_NET)}</TableCell>
                      <TableCell align="right">{formatNumber(row.CUPON_DM)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {formatNumber(row.TOTAL_DIA)}
                      </TableCell>
                      <TableCell align="center">
                        {row.FACTURAS_anuladas > 0 ? (
                          <Chip 
                            label={row.FACTURAS_anuladas} 
                            size="small"
                            color="error"
                            variant="outlined"
                            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                          />
                        ) : '0'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: row.FACTURAS_anuladas_TOTAL > 0 ? 'error.main' : 'inherit' }}>
                        {formatNumber(row.FACTURAS_anuladas_TOTAL)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Fila expandible con detalles de facturas */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={22}>
                        <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            {getFacturasVigentes(row).length > 0 ? (
                              renderDetalleFacturas(getFacturasVigentes(row))
                            ) : (
                              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                  Panel 1 - Detalle V
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                  No hay facturas vigentes para este corte
                                </Typography>
                              </Paper>
                            )}
                            {renderDetalleFacturasAnuladas(getFacturasAnuladas(row))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {datosAPI && !datosFiltrados.length && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#999', marginBottom: 16 }} />
          <Typography variant="h6" color="textSecondary">
            No se encontraron datos para los filtros seleccionados
          </Typography>
        </Paper>
      )}

      {/* Diálogo de detalle */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        {selectedRow && (
          <>
            <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <FontAwesomeIcon icon={faReceipt} />
                  <Typography variant="h6">
                    Detalle Completo - {selectedRow.Establecimiento || ''} - {selectedRow.Series || ''}
                  </Typography>
                </Box>
                <Button 
                  onClick={handleCloseDialog}
                  sx={{ color: 'white', minWidth: 'auto' }}
                >
                  ✕
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {/* Información General */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Información General
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Fecha</Typography>
                        <Typography variant="body1">
                          {selectedRow.DocDate ? new Date(selectedRow.DocDate).toLocaleDateString() : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Tienda</Typography>
                        <Typography variant="body1">{selectedRow.Establecimiento || ''}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Serie</Typography>
                        <Typography variant="body1">{selectedRow.Series || ''}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Total del Día</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          {formatCurrency(selectedRow.TOTAL_DIA)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Métodos de Pago */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Métodos de Pago
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <FontAwesomeIcon icon={faMoneyBill} style={{ color: '#4caf50' }} />
                              Efectivo
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.EFECTIVO)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <FontAwesomeIcon icon={faMoneyBill} style={{ color: '#ff9800' }} />
                              Cheques
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.CHEQUES)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <FontAwesomeIcon icon={faCreditCard} style={{ color: '#9c27b0' }} />
                              Credomatic
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.CREDOMATIC)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <FontAwesomeIcon icon={faCreditCard} style={{ color: '#2196f3' }} />
                              Cupón
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.CUPON)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Degustaciones</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.DEGUSTACIONES)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Empleados</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.EMPLEADOS)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Exención IVA</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.EXENCION_DE_IVA)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>NEO NET POS</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.NEO_NET_POS)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>NEO NET LINK</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.NEO_NET_LINK)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    &nbsp;
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Pedidos Ya</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.PEDIDOS_YA)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tienda en Línea</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.TIENDA_EN_LINEA_Shopify)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Transferencia</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.TRANSFERENCIA)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Transfer DM</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.TRANSFER_DM)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Visa Net</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.VISA_NET)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Cupón DM</TableCell>
                          <TableCell align="right">{formatCurrency(selectedRow.CUPON_DM)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Facturas Anuladas */}
                {(selectedRow.FACTURAS_anuladas > 0 || selectedRow.FACTURAS_anuladas_TOTAL > 0 || getFacturasAnuladas(selectedRow).length > 0) && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#ffebee', mt: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <FontAwesomeIcon icon={faTrash} style={{ color: '#d32f2f' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                          Facturas Anuladas
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Cantidad de Facturas</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {selectedRow.FACTURAS_anuladas || getFacturasAnuladas(selectedRow).length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Total Anulado</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                            {formatCurrency(getTotalAnulado(selectedRow))}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Paneles de detalle */}
                <Grid item xs={12}>
                  {getFacturasVigentes(selectedRow).length > 0 ? (
                    renderDetalleFacturas(getFacturasVigentes(selectedRow))
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        Panel 1 - Detalle V
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        No hay facturas vigentes para este corte
                      </Typography>
                    </Paper>
                  )}
                  {renderDetalleFacturasAnuladas(getFacturasAnuladas(selectedRow))}
                </Grid>

                {/* Detalle de Facturas Anuladas */}
                {false && getFacturasAnuladas(selectedRow).length > 0 && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
                      <FontAwesomeIcon icon={faTrash} style={{ color: '#d32f2f' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        Detalle de Facturas Anuladas ({getFacturasAnuladas(selectedRow).length})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#ffebee' }}>
                            <TableCell><strong>Factura Anulada</strong></TableCell>
                            <TableCell><strong>Factura Original</strong></TableCell>
                            <TableCell><strong>Fecha AnulaciÃ³n</strong></TableCell>
                            <TableCell align="right"><strong>Total</strong></TableCell>
                            <TableCell><strong>Caja</strong></TableCell>
                            <TableCell><strong>Serie</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>Canal</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getFacturasAnuladas(selectedRow).map((detalle, idx) => (
                            <TableRow key={idx} sx={{ bgcolor: '#fff5f5' }}>
                              <TableCell>{detalle.ID || detalle.DocNum || ''}</TableCell>
                              <TableCell>{detalle.ORIGINAL_RECEIPT_ID || 'N/A'}</TableCell>
                              <TableCell>{detalle.CREATEDAT ? new Date(detalle.CREATEDAT).toLocaleString() : 'N/A'}</TableCell>
                              <TableCell align="right">{formatCurrency(detalle.TOTALGROSSAMOUNT || detalle.DocTotal || detalle.Total || 0)}</TableCell>
                              <TableCell>{detalle.POS_SYSTEM_NAME || detalle.TarjetaNombre || 'N/A'}</TableCell>
                              <TableCell>{detalle.SerieCajaAnulacion || detalle.SeriesName || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={detalle.STATUS || detalle.EstatusFactura || 'Anulada'}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  icon={<FontAwesomeIcon icon={faTimesCircle} />}
                                />
                              </TableCell>
                              <TableCell>{detalle.Canal || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Facturas Detalladas */}
                {false && selectedRow.DETALLE && Array.isArray(selectedRow.DETALLE) && selectedRow.DETALLE.length > 0 && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
                      <FontAwesomeIcon icon={faReceipt} style={{ color: '#1976d2' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Detalle de Facturas ({selectedRow.DETALLE.length})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell><strong>Factura</strong></TableCell>
                            <TableCell><strong>Código</strong></TableCell>
                            <TableCell><strong>Cliente</strong></TableCell>
                            <TableCell align="right"><strong>Total</strong></TableCell>
                            <TableCell><strong>Tarjeta</strong></TableCell>
                            <TableCell><strong>Cuenta</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>Canal</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRow.DETALLE.map((detalle, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{detalle.DocNum || ''}</TableCell>
                              <TableCell>{detalle.CardCode || ''}</TableCell>
                              <TableCell>{detalle.CardName || 'N/A'}</TableCell>
                              <TableCell align="right">{formatCurrency(detalle.DocTotal || detalle.Total || 0)}</TableCell>
                              <TableCell>
                                {detalle.TarjetaNombre || 'N/A'}
                                {detalle.TarjetaNombre?.toLowerCase().includes('efectivo') && (
                                  <FontAwesomeIcon icon={faMoneyBill} style={{ marginLeft: 4, color: '#4caf50' }} size="xs" />
                                )}
                              </TableCell>
                              <TableCell>{detalle.AcctName || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={detalle.EstatusFactura || 'N/A'} 
                                  size="small"
                                  color={detalle.EstatusFactura === 'V' ? 'success' : 'error'}
                                  variant="outlined"
                                  icon={detalle.EstatusFactura === 'V' ? 
                                    <FontAwesomeIcon icon={faCheckCircle} /> : 
                                    <FontAwesomeIcon icon={faTimesCircle} />
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {detalle.Canal || 'N/A'}
                                {detalle.Canal === 'DOM' && (
                                  <FontAwesomeIcon icon={faExchangeAlt} style={{ marginLeft: 4, color: '#ff9800' }} size="xs" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
}

export default ReporteCortez;
